/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import type { Location } from 'history';
import URI from 'jsuri';

import API from 'js/lib/api';
import * as redirect from 'js/lib/coursera.redirect';
import createLoadableComponent from 'js/lib/createLoadableComponent';
import { useLocation, useNavigate, useParams, useRouter } from 'js/lib/useRouter';
import user from 'js/lib/user';

import {
  Button,
  Grid,
  LoadingSection,
  PageGridContainer,
  PageNotification,
  Typography,
  Typography2,
  breakpoints,
  typography,
  typography2,
} from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import EnterpriseInjectedSearchPageHeader from 'bundles/composable-page-header-enterprise/header-renderers/EnterpriseInjectedSearchPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { handleRedirect } from 'bundles/enterprise-learner-onboarding/utils';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import { handleProgramRedirect } from 'bundles/enterprise-legacy-learner-home/utils/handleProgramRedirect';
import MyPrograms from 'bundles/org-landing/components/MyPrograms';
import OrgLandingContext from 'bundles/org-landing/components/OrgLandingContext';
import SponsorLogos from 'bundles/org-landing/components/SponsorLogos';
import type { ImageFragment } from 'bundles/org-landing/components/__generated__/contentfulTypes';
import useOrgLandingQuery from 'bundles/org-landing/components/utils/useOrgLandingQuery';
import { useProductFeatures } from 'bundles/org-landing/productFeatureFlags';
import { IdTypes } from 'bundles/product-features';
import { getSearchBar } from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';

import _t from 'i18n!nls/org-landing';

const AuthenticationOption = createLoadableComponent(
  () => import('bundles/user-account/components/AuthenticationOption')
);

type ImageCollection =
  | {
      items: Array<ImageFragment | null | undefined>;
    }
  | null
  | undefined;

function isImageCollectionEmpty(collection: ImageCollection) {
  return !collection || collection.items.length === 0;
}

function getFontStyle(fontName?: string | null) {
  switch (fontName) {
    case 'd1semibold':
      return typography.d1semibold;
    case 'd1':
      return typography.d1;
    case 'd2semibold':
      return typography.d2semibold;
    case 'd2':
      return typography.d2;
    case 'h1semibold':
      return typography.h1semibold;
    case 'h1':
      return typography.h1;
    case 'h2semibold':
      return typography.h2semibold;
    case 'h2':
      return typography.h2;
    case 'h3semibold':
      return typography2.subtitleMedium;
    case 'h3bold':
      return typography2.subtitleMedium;
    case 'h4bold':
      return typography2.subtitleMedium;
    case 'body1':
      return typography2.bodyPrimary;
    case 'body2':
      return typography2.bodySecondary;
    default:
      return null;
  }
}

function LogoList({ logos }: { logos: ImageCollection }) {
  const styles = {
    container: css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-evenly;

      img {
        max-height: 128px;
        max-width: 90vw;
      }
    `,
  };

  if (isImageCollectionEmpty(logos)) {
    return null;
  }

  return (
    <div css={styles.container}>
      {logos?.items.map((logo) =>
        logo ? <img key={logo.url} src={logo.url ?? ''} alt={logo.description ?? ''} /> : null
      )}
    </div>
  );
}

function OrgLandingPage() {
  const [isProcessingRedirect, setIsProcessingRedirect] = useState<boolean>(true);
  const [isInitiatingSsoLogin, setIsInitiatingSsoLogin] = useState<boolean>(false);
  const styles = {
    root: css`
      width: 100%;
      margin: auto var(--cds-spacing-800);
      background-color: var(--cds-color-blue-700);
    `,
    errorBlock: css`
      margin-bottom: var(--cds-spacing-800);
      margin-top: calc(var(--cds-spacing-600) * -1);

      ${breakpoints.down('xs')} {
        margin-top: calc(var(--cds-spacing-800) * -1);
        margin-left: calc(var(--cds-spacing-200) * -1);
        margin-right: calc(var(--cds-spacing-200) * -1);
      }
    `,

    headerBlock: css`
      padding: var(--cds-spacing-800) 0;
    `,

    headerContent: css`
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      margin: 0 auto;
    `,

    loggedInHeaderContent: css`
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      justify-content: space-between;

      ${breakpoints.down('sm')} {
        flex-direction: column;
      }
    `,

    loggedInHeaderHeading: css`
      flex-grow: 1;
      margin-right: var(--cds-spacing-800);
    `,

    brandLogo: css`
      width: 80%;
      margin-bottom: var(--cds-spacing-200);
    `,

    brandHeadline: css`
      margin-bottom: var(--cds-spacing-200);

      ${breakpoints.down('xs')} {
        font-size: 30px;
        line-height: 34px;
      }
    `,

    brandTagline: css`
      margin-bottom: var(--cds-spacing-400);
      max-width: 656px;

      ${breakpoints.down('xs')} {
        font-size: 14px;
        line-height: 18px;
      }
    `,

    welcomeHeadline: css`
      font-size: 48px;
      line-height: 52px;
      margin-bottom: var(--cds-spacing-200);

      ${breakpoints.down('xs')} {
        font-size: 30px;
        line-height: 34px;
      }
    `,

    welcomeTagline: css`
      font-size: 16px;
      line-height: 24px;

      ${breakpoints.down('xs')} {
        font-size: 14px;
        line-height: 18px;
      }
    `,

    bodyBlock: css`
      margin: var(--cds-spacing-400) auto;
      border-radius: 4px;
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      background: var(--cds-color-neutral-background-primary);
      padding: var(--cds-spacing-400);

      ${breakpoints.down('xs')} {
        padding: 0;
      }
    `,

    bodyImage: css`
      max-width: 100%;
      border-radius: 8px;

      ${breakpoints.down('xs')} {
        border-radius: 0;
      }
    `,

    bodyContent: css`
      display: flex;
      flex-direction: column;
      justify-content: center;

      ${breakpoints.down('xs')} {
        margin: 0 var(--cds-spacing-400) var(--cds-spacing-400);
      }
    `,

    markdownWrapper: css`
      /* we need to override the markdown styles to fit CDS design */
      .rc-Markdown.styled {
        /* set base font style */
        ${typography2.bodyPrimary}

        /* cancel p styles from markdown */
        p {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          line-height: inherit;
          min-height: auto;
          margin-bottom: var(--cds-spacing-50);
        }

        h1 {
          ${typography.h1}
        }

        h2 {
          ${typography.h2}
        }

        h3 {
          ${typography2.subtitleMedium}
        }

        /* cancel blockquote styles from markdown */
        blockquote {
          /* blockquote is usually used as the first element as a quote, so we use h1 style here */
          border: 0;
          padding: 0;

          /* add quotes */
          p {
            /* when multiple paragraphs are in the blockquote, we need to add open quote to every paragraph */
            &:first-child::before {
              content: '\u201c';
            }

            /* when multiple pagaraphs are in the blockquote, only the last paragraph needs close quote */
            &:last-child::after {
              content: '\u201d';
            }
          }
        }
      }
    `,

    errorBody: css`
      .rc-Markdown.styled {
        p {
          font: ${typography2.bodyPrimary};
          margin-bottom: 0;
          min-height: auto;
        }
      }
    `,
  };

  const { thirdPartyOrganization, isPreview, locale } = React.useContext(OrgLandingContext);

  const router = useRouter();
  const navigate = useNavigate();
  const { query } = useLocation();
  const { orgSlug } = useParams();

  const userId = user.get().id;
  const { features: productFeatures } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  const showAuthModal = Boolean(query.authMode);

  const [isErrorVisible, setIsErrorVisible] = useState(true);

  const { enterprisePrograms, softDeletedPrograms, programInvitations, membershipsMap, contentfulData, isLoading } =
    useOrgLandingQuery({
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      orgSlug,
      orgId: thirdPartyOrganization?.id,
      userId,
      locale,
      isPreview,
    });

  useEffect(() => {
    if (isLoading || !contentfulData) {
      return;
    }

    const redirectPath = handleProgramRedirect(userId, enterprisePrograms, programInvitations);
    if (redirectPath) {
      handleRedirect(redirectPath, router);
    } else {
      setIsProcessingRedirect(false);
    }
  }, [contentfulData, enterprisePrograms, isLoading, programInvitations, router, userId]);

  if (isLoading || isProcessingRedirect) {
    return <LoadingSection label={_t('Loading')} />;
  }

  if (!contentfulData) {
    return <LoadingSection hasError label={_t('Failed to load page')} />;
  }

  // if `attemptSSOLogin is set`, redirect to the SSO login page
  if (query.attemptSSOLogin && !userId && thirdPartyOrganization.id && thirdPartyOrganization.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrganization.id} />;
  }

  const isValidUser =
    userId && (enterprisePrograms.length > 0 || programInvitations.length > 0 || softDeletedPrograms.length > 0);

  const brandingData = contentfulData.orgHomepageOrgLandingCollection?.items?.[0];
  if (!brandingData) {
    return <LoadingSection hasError label={_t('Failed to load page')} />;
  }

  // globalStyle applies to the main container as the default styles
  const globalStyle: Record<string, string> = {};
  if (brandingData.textColor) {
    globalStyle.color = brandingData.textColor;
  }

  const bannerStyle: Record<string, string> = {};
  if (brandingData.brandColor) {
    bannerStyle.backgroundColor = brandingData.brandColor;
  }

  if (brandingData.brandTextColor) {
    bannerStyle.color = brandingData.brandTextColor;
  }

  const brandHeadlineExtraStyle: Record<string, string> = {};
  const headerContentExtraStyle: Record<string, string> = {};
  if (brandingData.headlineTextAlign === 'left') {
    brandHeadlineExtraStyle.textAlign = 'left';
    headerContentExtraStyle.textAlign = 'left';
    headerContentExtraStyle.alignItems = 'flex-start';
  } else if (brandingData.headlineTextAlign === 'right') {
    brandHeadlineExtraStyle.textAlign = 'right';
    headerContentExtraStyle.textAlign = 'right';
    headerContentExtraStyle.alignItems = 'flex-end';
  } else {
    brandHeadlineExtraStyle.textAlign = 'center';
    headerContentExtraStyle.textAlign = 'center';
    headerContentExtraStyle.alignItems = 'center';
  }

  const bannerBodyFont = getFontStyle(brandingData.bannerBodyFont);
  const bannerBodyFirstElementFont = getFontStyle(brandingData.bannerBodyFirstElementFont);

  const bannerBodyStyle = brandingData.bannerBodyFont
    ? css`
        .rc-Markdown.styled {
          ${bannerBodyFont}
          font-weight: inherit;
        }
      `
    : null;
  const bannerBodyFirstElementStyle = brandingData.bannerBodyFirstElementFont
    ? css`
        .rc-Markdown.styled {
          & > *:first-child {
            ${bannerBodyFirstElementFont} font-weight: inherit;
          }
        }
      `
    : null;

  // add the search bar
  const searchBar = userId ? getSearchBar(enterprisePrograms, orgSlug ?? '', false, true) : null;

  const redirectToLoginFlow = () => {
    // preserve existing query params
    const newQuery: Location['query'] = {
      ...query,
      authMode: 'login',
      r: `/programs/all/${orgSlug}`,
    };

    if (thirdPartyOrganization.loginMethod === 'SAML') {
      newQuery.authProvider = thirdPartyOrganization.slug;
    }

    const qs = new URLSearchParams(newQuery).toString();
    navigate({
      pathname: `/programs/landing/${orgSlug}`,
      search: `?${qs}`,
    });
  };

  const handleGetStarted = () => {
    // If user has no programs and org is using SAML SSO login
    // Allow SAML SSO login to be initiated in case the org has SSO based program provisioning enabled
    const shouldInitiateSsoLogin =
      !isValidUser &&
      thirdPartyOrganization.loginMethod === 'SAML' &&
      (query.attemptSSOLogin === 'true' ||
        productFeatures.get('enterprise', 'enableForceSsoLoginV2Beta') ||
        productFeatures.get('enterprise', 'enableUnifiedSso'));

    if (shouldInitiateSsoLogin) {
      setIsInitiatingSsoLogin(true);

      // Create a new URI with current query parameters, but remove attemptSSOLogin
      const currentQuery = { ...query };
      delete currentQuery.attemptSSOLogin;
      const params = new URLSearchParams(currentQuery).toString();
      const queryString = params ? `?${params}` : '';

      const uri = new URI()
        .addQueryParam('action', 'initiateLoginFlow')
        .addQueryParam('id', thirdPartyOrganization.id)
        .addQueryParam('returnTo', redirect.getPathname() + queryString);

      API('/api/thirdPartyOrganizations.v1')
        .post(uri.toString())
        .then((res: string) => {
          window.location.assign(res);
        })
        .catch(() => {
          // If API call fails, fall back to regular login flow
          setIsInitiatingSsoLogin(false);
          redirectToLoginFlow();
        });
    } else {
      redirectToLoginFlow();
    }
  };

  const handleDismissError = () => {
    setIsErrorVisible(false);
  };

  let error: React.ReactNode = null;
  if (userId && !isValidUser) {
    const errorTitle = brandingData.noProgramsErrorTitle ?? _t("It looks like you haven't joined any programs.");
    let errorBody =
      brandingData.noProgramsErrorBody ??
      _t(
        'Hi {{email}}. It looks like you haven’t joined any programs. If you believe this is in error, confirm you are signed in with the email address that your original program invitation was sent to. For more information, contact your program’s administrator.'
      );
    const u = user.get();
    errorBody = errorBody.replace('{{email}}', u.email_address);
    errorBody = errorBody.replace('{{name}}', u.display_name);
    error = (
      <PageNotification severity="information" title={errorTitle} onDismiss={handleDismissError} aria-live="polite">
        <div css={styles.errorBody}>
          <Markdown source={errorBody} />
        </div>
      </PageNotification>
    );
  }

  return (
    <React.Fragment>
      {isValidUser && (
        <React.Fragment>
          <EnterpriseInjectedSearchPageHeader injectedSearch={searchBar} />
          <UniversalPageFeatures />
        </React.Fragment>
      )}
      {/* MERCH-241: the header should not be displayed on the logged out page.
       * However, the auth form is loaded in the PageHeader.
       * So here we load the auth form directly if user is not logged in.
       */}
      {!isValidUser && showAuthModal && <AuthenticationOption key="authenticationOption" isEnterprise={true} />}
      <main css={globalStyle}>
        {/* Banner */}
        {!isValidUser && (
          <div css={[styles.headerBlock, bannerStyle]}>
            <PageGridContainer maxWidth="lg">
              {error && isErrorVisible && (
                <Grid item xs={12}>
                  <div css={styles.errorBlock}>{error}</div>
                </Grid>
              )}
              <Grid item xs={12}>
                <div css={[styles.headerContent, headerContentExtraStyle]}>
                  {!isImageCollectionEmpty(brandingData.logosCollection) && (
                    <div css={styles.brandLogo}>
                      <LogoList logos={brandingData.logosCollection} />
                    </div>
                  )}
                  <Typography
                    variant="d1semibold"
                    color="inherit"
                    css={[styles.brandHeadline, brandHeadlineExtraStyle]}
                  >
                    {brandingData.headline?.trim()}
                  </Typography>
                  {brandingData.subHeadline && (
                    <Typography variant="h2" color="inherit" css={styles.brandTagline}>
                      {brandingData.subHeadline?.trim()}
                    </Typography>
                  )}
                  {brandingData.tagline && (
                    <Typography2 component="p" variant="bodyPrimary" color="inherit" css={styles.brandTagline}>
                      {brandingData.tagline?.trim()}
                    </Typography2>
                  )}
                  <Button
                    variant="primaryInvert"
                    size="medium"
                    onClick={handleGetStarted}
                    loading={isInitiatingSsoLogin}
                  >
                    {brandingData.getStartedLabel?.trim() ?? _t('Get Started')}
                  </Button>
                </div>
              </Grid>
            </PageGridContainer>
          </div>
        )}

        {/* Logged in banner */}
        {isValidUser && (
          <div css={[styles.headerBlock, bannerStyle]}>
            <PageGridContainer maxWidth="lg">
              <Grid item xs={12}>
                <div css={styles.loggedInHeaderContent}>
                  <div>
                    {(!isImageCollectionEmpty(brandingData.logosLoggedInCollection) ||
                      !isImageCollectionEmpty(brandingData.logosCollection)) && (
                      <div css={styles.brandLogo}>
                        {!isImageCollectionEmpty(brandingData.logosLoggedInCollection) ? (
                          <LogoList logos={brandingData.logosLoggedInCollection} />
                        ) : (
                          <LogoList logos={brandingData.logosCollection} />
                        )}
                      </div>
                    )}
                  </div>

                  <div css={[styles.loggedInHeaderHeading, styles.markdownWrapper]}>
                    <Typography variant="d1semibold" color="inherit" css={styles.welcomeHeadline}>
                      {_t('Hello, #{name}', { name: user.get().full_name })}
                    </Typography>
                    <Markdown source={brandingData.taglineLoggedIn?.trim() ?? brandingData.tagline?.trim()} />
                  </div>
                </div>
              </Grid>
            </PageGridContainer>
          </div>
        )}

        {/* Body (logged out) */}
        {!isValidUser && brandingData.bannerBody && (
          <div>
            <PageGridContainer maxWidth="lg">
              <Grid item xs={12}>
                <div css={styles.bodyBlock}>
                  <Grid container spacing={32}>
                    <Grid item xs={12} sm={6}>
                      {brandingData.bannerImage && (
                        <img
                          src={brandingData.bannerImage.url ?? ''}
                          alt={brandingData.bannerImage.description ?? ''}
                          css={styles.bodyImage}
                        />
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      css={[styles.bodyContent, styles.markdownWrapper, bannerBodyStyle, bannerBodyFirstElementStyle]}
                    >
                      <Markdown source={brandingData.bannerBody} />
                    </Grid>
                  </Grid>
                </div>
              </Grid>
            </PageGridContainer>
          </div>
        )}

        {/* Sponser logos */}
        {!isValidUser && <SponsorLogos logos={brandingData.sponsorLogosCollection?.items ?? []} />}

        {isValidUser && (
          <MyPrograms
            header={brandingData.programListHeader?.trim()}
            enterprisePrograms={enterprisePrograms}
            softDeletedPrograms={softDeletedPrograms}
            programInvitations={programInvitations}
            membershipsMap={membershipsMap}
          />
        )}
      </main>
    </React.Fragment>
  );
}

export default OrgLandingPage;
